import type { ClusterSnapshot } from "../../types";
import { AreaChart } from "../charts/AreaChart";
import { GaugeArc } from "../charts/GaugeArc";
import { fmtDuration } from "../../utils/format";
import { CheatsheetButton } from "../CheatsheetButton";
import { useHoverContext } from "../../cheatsheets/provider";
import type { CheatsheetCtx } from "../../cheatsheets/types";

interface Props {
  snapshot: ClusterSnapshot;
  nodeVar: string;
  setNodeVar: (n: string) => void;
  onDrillJob: (id: number) => void;
}

const last = (arr: number[]): number => (arr.length ? arr[arr.length - 1] : 0);

export function NodeDrilldown({ snapshot, nodeVar, setNodeVar, onDrillJob }: Props) {
  const nd = snapshot.nodes.find((n) => n.name === nodeVar) ?? snapshot.nodes[0];
  if (!nd) {
    return (
      <div className="panel">
        <div className="panel-body" style={{ padding: 40, textAlign: "center", color: "var(--fg-2)" }}>
          No nodes in snapshot.
        </div>
      </div>
    );
  }
  const nodeGpus = snapshot.gpus.filter((g) => g.node === nd.name);
  const nodePorts = snapshot.ib_ports.filter((p) => p.node === nd.name);
  const nodeJobs = snapshot.jobs.filter(
    (j) => j.nodes.includes(nd.name) && j.state === "RUNNING"
  );
  const hcaIndex = snapshot.nodes.findIndex((n) => n.name === nd.name);

  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(12, 1fr)", gap: 12 }}>
      {/* top bar */}
      <div className="panel" style={{ gridColumn: "span 12" }}>
        <div
          className="panel-body"
          style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 16 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: "var(--fg-3)",
              fontFamily: "var(--mono)",
            }}
          >
            $node
          </div>
          <select
            value={nd.name}
            onChange={(e) => setNodeVar(e.target.value)}
            style={{
              height: 26,
              padding: "0 10px",
              background: "var(--bg-3)",
              border: "1px solid var(--line-2)",
              borderRadius: 4,
              color: "var(--fg-0)",
              fontFamily: "var(--mono)",
              fontSize: 12,
            }}
          >
            {snapshot.nodes.map((n) => (
              <option key={n.name} value={n.name}>
                {n.name}
              </option>
            ))}
          </select>
          <div className="divider" />
          <div
            style={{
              display: "flex",
              gap: 18,
              fontSize: 11,
              fontFamily: "var(--mono)",
            }}
          >
            <MetaPair
              k="state"
              v={nd.state.toUpperCase()}
              color={nd.state === "down" ? "var(--rose)" : "var(--emerald)"}
            />
            <MetaPair k="cpu" v={`${nd.sockets}×${nd.cores}c·${nd.threads}t`} />
            <MetaPair k="ram" v={`${nd.ram_gb} GiB`} />
            <MetaPair k="gpus" v={`${nodeGpus.length}× ${nodeGpus[0]?.model ?? "—"}`} />
            <MetaPair
              k="hca"
              v={`mlx5_${hcaIndex} · ${nodePorts[0]?.speed_gb ?? "—"}Gb`}
            />
            <MetaPair
              k="uptime"
              v={fmtDuration((Date.now() - nd.booted_at) / 1000)}
            />
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", gap: 4 }}>
            {snapshot.nodes.map((n) => (
              <button
                key={n.name}
                className={`btn xs ${n.name === nd.name ? "primary" : ""}`}
                onClick={() => setNodeVar(n.name)}
              >
                {n.name.replace("hpc-node-", "n")}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* system row */}
      <SysCard
        col="span 3"
        title="CPU util"
        value={`${last(nd.cpu_util).toFixed(1)}%`}
        data={nd.cpu_util}
        color="var(--cyan)"
        yMax={100}
        unit="%"
      />
      <SysCard
        col="span 3"
        title="RAM used"
        value={`${((last(nd.mem_util) * nd.ram_gb) / 100).toFixed(0)} GiB`}
        sub={`${last(nd.mem_util).toFixed(0)}% of ${nd.ram_gb}`}
        data={nd.mem_util}
        color="var(--violet)"
        yMax={100}
        unit="%"
      />
      <SysCard
        col="span 3"
        title="Load avg (1m)"
        value={last(nd.load1).toFixed(2)}
        sub={`${nd.threads} threads · ratio ${(last(nd.load1) / Math.max(nd.threads, 1)).toFixed(3)}`}
        data={nd.load1}
        color="var(--amber)"
        yMax={Math.max(4, ...nd.load1)}
      />
      <SysCard
        col="span 3"
        title="Disk I/O"
        value={`${(last(nd.disk_read_mbs) + last(nd.disk_write_mbs)).toFixed(0)} MB/s`}
        sub={`R ${last(nd.disk_read_mbs).toFixed(0)} · W ${last(nd.disk_write_mbs).toFixed(0)}`}
        data={nd.disk_read_mbs.map((v, i) => v + nd.disk_write_mbs[i])}
        color="var(--blue)"
        yMax={200}
      />

      {/* per-GPU detail */}
      {nodeGpus.map((g, i) => (
        <PanelWrap
          key={g.id}
          gridColumn="span 6"
          ctx={{
            card: "gpu_card",
            title: g.id,
            subtitle: `${g.model} · ${g.status}`,
            data: { node: g.node, gpu_idx: g.gpu_id, gpu_id: g.id },
          }}
        >
          <div className="panel-hd">
            <span className="panel-title">{g.id}</span>
            <span className="chip">{g.model || "GPU"}</span>
            <span className="chip">PCIe gen6 x16</span>
            {g.status === "xid-faulted" ? (
              <span className="chip crit">
                <span className="dot pulse" />
                XID FAULT
              </span>
            ) : (
              <span className="chip ok">
                <span className="dot" />
                healthy
              </span>
            )}
            <div className="panel-meta">
              device{i} · bus 0000:{(i + 1).toString(16).padStart(2, "0")}:00.0
            </div>
          </div>
          <div className="panel-body" style={{ padding: 14 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 14,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <GaugeArc
                  value={last(g.util)}
                  max={100}
                  unit="%"
                  label="UTIL"
                  size={104}
                  color="var(--cyan)"
                  thresholdWarn={95}
                />
                <div className="chip" style={{ fontSize: 9 }}>
                  power {(last(g.power_w) / 1000).toFixed(2)}kW / 2.40
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <MiniStat
                  label="TEMP"
                  value={`${last(g.temp_c).toFixed(0)}°C`}
                  color={
                    last(g.temp_c) > 85
                      ? "var(--rose)"
                      : last(g.temp_c) > 75
                      ? "var(--amber)"
                      : "var(--cyan)"
                  }
                  bar={last(g.temp_c) / 100}
                />
                <MiniStat
                  label="HBM"
                  value={`${(last(g.mem_mb) / 1024).toFixed(1)}/${(g.mem_total_mb / 1024).toFixed(0)} GiB`}
                  color="var(--violet)"
                  bar={last(g.mem_mb) / g.mem_total_mb}
                />
                <MiniStat
                  label="NVLINK 6"
                  value={`${(last(g.nvlink_gbs) / 1000).toFixed(2)} TB/s`}
                  color="var(--blue)"
                  bar={last(g.nvlink_gbs) / 3600}
                  sub="of 3.6 TB/s"
                />
                <MiniStat
                  label="POWER"
                  value={`${(last(g.power_w) / 1000).toFixed(2)} kW`}
                  color="var(--amber)"
                  bar={last(g.power_w) / 2400}
                  sub="of 2.4 kW pkg TDP"
                />
              </div>
            </div>
            <div style={{ marginTop: 10, height: 84 }}>
              <AreaChart
                series={[{ label: "util", data: g.util, color: "var(--cyan)" }]}
                height={84}
                yMax={100}
                unit="%"
                xTicks={4}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: 14,
                marginTop: 4,
                fontSize: 10,
                color: "var(--fg-2)",
                fontFamily: "var(--mono)",
              }}
            >
              <span>
                XID ·{" "}
                <span className={g.xid_count ? "sev-crit" : ""}>{g.xid_count}</span>
              </span>
              <span>
                ECC SBE ·{" "}
                <span className={g.ecc_sbe ? "sev-warn" : ""}>{g.ecc_sbe}</span>
              </span>
              <span>
                ECC DBE ·{" "}
                <span className={g.ecc_dbe ? "sev-crit" : ""}>{g.ecc_dbe}</span>
              </span>
              <span className="u-xdim">persistence-mode: ON · compute-mode: Default</span>
            </div>
          </div>
        </PanelWrap>
      ))}

      {/* IB per-port */}
      <PanelWrap
        gridColumn="span 7"
        ctx={{
          card: "ib_port",
          title: `InfiniBand · ${nd.name}`,
          subtitle: `HCA mlx5_${hcaIndex} · ${nodePorts.length} ports`,
          data: {
            node: nd.name,
            hca: `mlx5_${hcaIndex}`,
            port: nodePorts[0]?.port,
          },
        }}
      >
        <div className="panel-hd">
          <span className="panel-title">InfiniBand · per-port throughput</span>
          <span className="chip cyan">HCA mlx5_{hcaIndex}</span>
          <span className="chip">{nodePorts.length} ports</span>
          <div className="panel-meta">
            RDMA · {nodePorts[0]?.speed_gb ?? 0}Gb
            {nodePorts[0]?.speed_gb === 800
              ? " (XDR)"
              : nodePorts[0]?.speed_gb === 400
              ? " (NDR)"
              : nodePorts[0]?.speed_gb === 200
              ? " (HDR)"
              : ""} · PFC on
          </div>
        </div>
        <div className="panel-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {nodePorts.map((p, i) => (
              <div
                key={i}
                style={{
                  background: "var(--bg-3)",
                  border: "1px solid var(--line)",
                  borderRadius: 4,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  <span className="mono" style={{ fontSize: 11 }}>
                    port {p.port}
                  </span>
                  {p.state === 1 ? (
                    <span className="chip ok" style={{ fontSize: 9, height: 16 }}>
                      <span className="dot" />
                      ACTIVE
                    </span>
                  ) : (
                    <span className="chip crit" style={{ fontSize: 9, height: 16 }}>
                      <span className="dot pulse" />
                      DOWN
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <span className="mono u-xdim" style={{ fontSize: 10 }}>
                    {(p.xmit_bytes_total / 1e12).toFixed(2)}TB tx
                  </span>
                </div>
                <AreaChart
                  series={[
                    { label: "tx", data: p.xmit_gbs, color: "var(--cyan)" },
                    { label: "rx", data: p.recv_gbs, color: "var(--violet)" },
                  ]}
                  height={80}
                  yMax={Math.max(1, p.speed_gb / 8)}
                  unit=" GB/s"
                  xTicks={3}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    marginTop: 4,
                    fontSize: 10,
                    fontFamily: "var(--mono)",
                  }}
                >
                  <span>
                    <span style={{ color: "var(--cyan)" }}>▬</span> tx{" "}
                    {last(p.xmit_gbs).toFixed(1)}
                  </span>
                  <span>
                    <span style={{ color: "var(--violet)" }}>▬</span> rx{" "}
                    {last(p.recv_gbs).toFixed(1)}
                  </span>
                  <span style={{ color: "var(--amber)" }}>
                    sym errs · {p.symbol_errors_total}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 10,
              padding: 8,
              background: "var(--bg-3)",
              borderRadius: 4,
              fontSize: 10,
              fontFamily: "var(--mono)",
              color: "var(--fg-2)",
              lineHeight: 1.6,
            }}
          >
            <span className="u-xdim">ibstatus →</span> state:{" "}
            <span style={{ color: "var(--emerald)" }}>
              {nodePorts[0]?.state === 1 ? "4: ACTIVE" : "1: DOWN"}
            </span>{" "}
            · phys:{" "}
            <span style={{ color: "var(--emerald)" }}>5: LinkUp</span> · rate:{" "}
            {nodePorts[0]?.speed_gb ?? 0} Gb/s
          </div>
        </div>
      </PanelWrap>

      {/* running jobs on this node */}
      <PanelWrap
        gridColumn="span 5"
        ctx={{
          card: "queue",
          title: `Running on ${nd.name}`,
          subtitle: `${nodeJobs.length} jobs`,
          data: { node: nd.name },
        }}
      >
        <div className="panel-hd">
          <span className="panel-title">Running on this node</span>
          <span className="chip">{nodeJobs.length} jobs</span>
        </div>
        <div className="panel-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>JobID</th>
                <th>Name</th>
                <th>User</th>
                <th>Elapsed</th>
                <th>GPUs</th>
              </tr>
            </thead>
            <tbody>
              {nodeJobs.map((j) => {
                const jg = j.gpus.filter((gid) => gid.startsWith(nd.name));
                return (
                  <tr key={j.id} className="clickable" onClick={() => onDrillJob(j.id)}>
                    <td className="mono" style={{ color: "var(--cyan)" }}>
                      {j.id}
                    </td>
                    <td className="u-truncate" style={{ maxWidth: 160 }}>
                      {j.name}
                    </td>
                    <td className="mono u-dim">{j.user}</td>
                    <td className="mono">{fmtDuration(j.elapsed_s)}</td>
                    <td className="mono">{jg.length || "—"}</td>
                  </tr>
                );
              })}
              {!nodeJobs.length && (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      color: "var(--fg-3)",
                      padding: 20,
                    }}
                  >
                    No running jobs on this node
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PanelWrap>
    </div>
  );
}

function PanelWrap({
  gridColumn,
  ctx,
  children,
}: {
  gridColumn: string;
  ctx: CheatsheetCtx;
  children: React.ReactNode;
}) {
  const hover = useHoverContext(ctx);
  return (
    <div {...hover} className="panel" style={{ gridColumn, position: "relative" }}>
      <CheatsheetButton ctx={ctx} top={8} right={10} />
      {children}
    </div>
  );
}

function MetaPair({ k, v, color }: { k: string; v: string; color?: string }) {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "baseline" }}>
      <span className="u-xdim">{k}=</span>
      <span style={{ color: color ?? "var(--fg-0)" }}>{v}</span>
    </div>
  );
}

function SysCard({
  col,
  title,
  value,
  sub,
  data,
  color,
  yMax,
  unit,
}: {
  col: string;
  title: string;
  value: string;
  sub?: string;
  data: number[];
  color: string;
  yMax?: number;
  unit?: string;
}) {
  return (
    <div className="panel" style={{ gridColumn: col }}>
      <div className="panel-body" style={{ padding: 12 }}>
        <div className="kpi-label">{title}</div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            marginTop: 2,
          }}
        >
          <span className="kpi-val" style={{ color, fontSize: 22 }}>
            {value}
          </span>
        </div>
        {sub && (
          <div
            className="u-xdim mono"
            style={{ fontSize: 10, marginTop: 1 }}
          >
            {sub}
          </div>
        )}
        <div style={{ marginTop: 6, height: 50 }}>
          <AreaChart
            series={[{ data, color }]}
            height={50}
            yMax={yMax}
            unit={unit ?? ""}
            showAxis={false}
            xTicks={0}
            yTicks={2}
          />
        </div>
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  color,
  bar,
  sub,
}: {
  label: string;
  value: string;
  color: string;
  bar: number;
  sub?: string;
}) {
  return (
    <div
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--line)",
        borderRadius: 3,
        padding: "6px 8px",
      }}
    >
      <div className="kpi-label" style={{ fontSize: 9 }}>
        {label}
      </div>
      <div className="mono" style={{ fontSize: 13, color, fontWeight: 500 }}>
        {value}
      </div>
      <div
        style={{
          height: 3,
          background: "var(--bg-4)",
          borderRadius: 1.5,
          marginTop: 4,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.min(100, bar * 100)}%`,
            height: "100%",
            background: color,
            transition: "width 400ms",
          }}
        />
      </div>
      {sub && (
        <div
          className="u-xdim mono"
          style={{ fontSize: 9, marginTop: 2 }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}
